import React from "react";
import ReactDOM from "react-dom/client";
import AppErrorBoundary from "./components/AppErrorBoundary";
// import App from "./App";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import "./global.css";
// O reset global do sistema (`* { box-sizing: border-box }`, margens, fonte do body) mora neste
// arquivo e só entrava no pacote pela importação do componente LandingPage. Ao ocultar a landing
// (RN-015, 04/09/2026) o reset sumiu e todo bloco com padding passou a estourar a largura — cards do
// dashboard sobrepostos e cadastro mais largo que a página. Importado aqui, na mesma ordem de antes.
import "./components/LandingPage.css";
import reportWebVitals from "./reportWebVitals";
import { RouterProvider, useRouter } from "./contexts/RouterContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ChatLauncher from "./components/ChatLauncher";
import SessionLockDialog from "./components/SessionLockDialog";
import { redirectToLogin } from "./services/httpClient";
import { isReservedPathSegment } from "./utils/tenantAlias";
import { aliasFromHost } from "./services/httpClient";
import { ThemeProvider, createTheme } from "@mui/material";
import { buttons, colors, inputs } from "./theme/designSystem";
import Login from "./clients/Login";
import AliasRegister from "./clients/AliasRegister";
import Dashboard from "./clients/Dashboard";
import ProfessionalSchedule from "./clients/ProfessionalSchedule";
import PatientsList from "./clients/PatientsList";
import PatientRegister from "./clients/PatientRegister";
import PatientIntake from "./clients/PatientIntake";
import SetPassword from "./clients/SetPassword";
import PageModel from "./clients/PageModel";
import AdminPlans from "./admin/AdminPlans";
import AdminProfiles from "./admin/AdminProfiles";
import AdminFunctionalities from "./admin/AdminFunctionalities";
import AdminEntities from "./admin/AdminEntities";
import EntitySettings from "./clients/EntitySettings";
import EntityUsers from "./clients/EntityUsers";
import EntityServiceTypes from "./clients/EntityServiceTypes";
import EntityProfessionalTypes from "./clients/EntityProfessionalTypes";
import EntityBusinessHours from "./clients/EntityBusinessHours";
import EntityAccessRestriction from "./clients/EntityAccessRestriction";
import ReportScheduleAnalysis from "./clients/ReportScheduleAnalysis";
import ReportCancellations from "./clients/ReportCancellations";
import ReportEvolution from "./clients/ReportEvolution";
import ReportDre from "./clients/ReportDre";
import ReportCashFlow from "./clients/ReportCashFlow";
import ReportCashScenario from "./clients/ReportCashScenario";
import CashScenario from "./clients/CashScenario";
import AccountCategories from "./clients/AccountCategories";
import PaymentOrigins from "./clients/PaymentOrigins";
import ReportPaidNotAttended from "./clients/ReportPaidNotAttended";
import ReportDayAttendances from "./clients/ReportDayAttendances";
import ReportPackageSessions from "./clients/ReportPackageSessions";
import ReportProfessionalCapacity from "./clients/ReportProfessionalCapacity";
import ReportPatientSchedules from "./clients/ReportPatientSchedules";
import ReportOccupancy from "./clients/ReportOccupancy";
import ReportPatientFlow from "./clients/ReportPatientFlow";
import Rooms from "./clients/Rooms";
import Birthdays from "./clients/Birthdays";
import ReportReceipts from "./clients/ReportReceipts";
import WhatsAppMessages from "./clients/WhatsAppMessages";
import DaySchedule from "./clients/DaySchedule";
import PatientAssistant from "./clients/PatientAssistant";
import AssistantsHost, { isAssistantsPopout } from "./clients/Assistants";
import AiUsage from "./clients/AiUsage";
import ScheduleBlocks from "./clients/ScheduleBlocks";
import DaySchedulePrint from "./components/DaySchedulePrint";
import Checkin from "./clients/Checkin";
import RoomMap from "./clients/RoomMap";
import CrmFunnel from "./clients/CrmFunnel";
import CrmSettings from "./clients/CrmSettings";
import CrmCampaigns from "./clients/CrmCampaigns";
import WhatsAppChannels from "./clients/WhatsAppChannels";
import ProfessionalServiceValues from "./clients/ProfessionalServiceValues";
import ProfessionalAvailability from "./clients/ProfessionalAvailability";
import ProfessionalContracts from "./clients/ProfessionalContracts";
import ServiceProfessionalValues from "./clients/ServiceProfessionalValues";
import EntityTeams from "./clients/EntityTeams";
import EntityTaxRegimes from "./clients/EntityTaxRegimes";
import AiFeatureTerms from "./clients/AiFeatureTerms";
import AiAgents from "./clients/AiAgents";
import AiTools from "./clients/AiTools";
import Workflows from "./clients/Workflows";
import WorkflowEditor from "./clients/WorkflowEditor";
import WorkflowCatalog from "./clients/WorkflowCatalog";
import WorkflowView from "./clients/WorkflowView";
import PlaybookDocuments from "./clients/PlaybookDocuments";
import DocumentLibrary from "./clients/DocumentLibrary";
import DocumentView from "./clients/DocumentView";
import DreChartOfAccounts from "./clients/DreChartOfAccounts";
import ScheduleStatuses from "./clients/ScheduleStatuses";
import ClinicPeriods from "./clients/ClinicPeriods";
import TherapyGoalPrint from "./components/TherapyGoalPrint";
import SchedulePrint from "./components/SchedulePrint";
import Holidays from "./clients/Holidays";
import PatientDocumentTypes from "./clients/PatientDocumentTypes";
import HealthPlanOperators from "./clients/HealthPlanOperators";
import HealthPlans from "./clients/HealthPlans";
import PatientSensitiveData from "./clients/PatientSensitiveData";
import Institutions from "./clients/Institutions";
import CashBook from "./clients/CashBook";
import ReportPatientTracking from "./clients/ReportPatientTracking";
import ReportProfessionalCurriculum from "./clients/ReportProfessionalCurriculum";
import ReportPendingEvolutions from "./clients/ReportPendingEvolutions";
import ReportProfessionalProductivity from "./clients/ReportProfessionalProductivity";
import ReportProfessionalStatement from "./clients/ReportProfessionalStatement";
import ProductivityPrint from "./components/ProductivityPrint";
import ServiceValuesPrint from "./components/ServiceValuesPrint";
import ReportExclusions from "./clients/ReportExclusions";
import AssessmentForms from "./clients/AssessmentForms";
import AssessmentFormEditor from "./clients/AssessmentFormEditor";
import ContractTemplates from "./clients/ContractTemplates";
import SignatureSettings from "./clients/SignatureSettings";
import ContractTemplateEditor from "./clients/ContractTemplateEditor";
import ContractPrint from "./components/ContractPrint";
import ReportPatientContracts from "./clients/ReportPatientContracts";
import AssessmentFormFamily from "./clients/AssessmentFormFamily";
import AssessmentApplication from "./clients/AssessmentApplication";
import Opportunities from "./clients/Opportunities";
import ChangePassword from "./clients/ChangePassword";
import AdminFaq from "./admin/AdminFaq";
import AdminProfessionalTypes from "./admin/AdminProfessionalTypes";
import UserProfile from "./clients/UserProfile";
import Faq from "./clients/Faq";
import EvolutionsPrint from "./components/EvolutionsPrint";
import DocumentsPrint from "./components/DocumentsPrint";
import PaymentReceiptPrint from "./components/PaymentReceiptPrint";
import PatientFinancialPrint from "./components/PatientFinancialPrint";
import PatientFinancialPage from "./clients/PatientFinancialPage";
import NotesPrint from "./components/NotesPrint";
import AssessmentPrint from "./components/AssessmentPrint";

// Tema global do Material-UI
const globalTheme = createTheme({
  // A cor da marca passa a ser a primária do MUI: sem isto, todo botão, checkbox, rádio e
  // aba precisava declarar o ciano à mão — 85 botões faziam exatamente isso — e o que
  // esquecia saía no azul padrão da biblioteca (RN-539i).
  palette: {
    primary: { main: colors.primary, dark: colors.primaryHover, contrastText: colors.white },
    error: { main: colors.error },
    success: { main: colors.success },
    warning: { main: colors.warning },
  },
  components: {
    // Padrão do botão em um lugar só. Eram 293 botões: 4 declaravam altura, 36 o canto e
    // 115 o textTransform — o resto herdava o padrão do MUI, que é caixa alta, 36px e
    // canto de 4px. Nenhum deles seguia o design system por acidente.
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: buttons.primary.textTransform,
          borderRadius: buttons.primary.borderRadius,
          height: buttons.primary.height,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: {
          height: buttons.small.height,
          fontSize: buttons.small.fontSize,
        },
        sizeLarge: {
          height: buttons.large.height,
          fontSize: buttons.large.fontSize,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: inputs.default.textColor,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:not(.MuiInputBase-multiline)": {
            height: inputs.default.height,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: inputs.default.focus.borderColor,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: inputs.default.focus.borderColor,
            boxShadow: inputs.default.focus.boxShadow,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: inputs.default.focus.labelColor,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          "&:focus": {
            backgroundColor: "transparent",
          },
          "&.Mui-disabled": {
            // Contraste vem da cor, não de opacidade: opacity apaga o valor inteiro
            WebkitTextFillColor: "#6c757d",
            opacity: 1,
            "& + .MuiSelect-nativeInput": {
              display: "none",
            },
          },
        },
        nativeInput: {
          "&:disabled": {
            display: "none",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected.Mui-disabled": {
            opacity: 1,
          },
        },
      },
    },
  },
});

const queryClient = new QueryClient();

// URL curta por tenant: /<alias> (sem ?page) -> guarda o alias no sessionStorage e vai direto ao login
// com a URL limpa (o usuário não consegue trocar o alias pela URL). O Login valida o alias na base.
// Usa window.location.origin para funcionar em qualquer ambiente/domínio.
// URL curta por tenant: /<alias> (sem ?page) -> guarda o alias e mostra o login MANTENDO /<alias> na barra.
// O roteador trata path-alias como página de login (ver getCurrentPageFromURL). Após logar, navega para ?page=...
const tenantAlias = window.location.pathname.split("/").filter(Boolean)[0];
// /login é nome de página, não alias de clínica — não pode ser guardado como tenant
if (tenantAlias && !isReservedPathSegment(tenantAlias) && !new URLSearchParams(window.location.search).has("page")) {
  sessionStorage.setItem("clinic4us-clinic-alias", tenantAlias);
}

// Clinica no SUBDOMINIO (ninho.clinic4us.com.br) -- RN-001c. Guardar aqui, na entrada, faz o
// alias valer para TODO codigo que le o sessionStorage direto, nao so para quem chama o
// resolvedor. E sobrescreve de proposito: trocar de clinica pelo endereco tem de vencer a marca
// da anterior, senao a proxima requisicao sai com o alias errado.
const hostTenant = aliasFromHost();
if (hostTenant) {
  sessionStorage.setItem("clinic4us-clinic-alias", hostTenant);
}

// Domínio raiz (ex-Bubble, desativado em 05/09/2026) e www levam ao endereço canônico do app,
// preservando caminho e query (convite, alias, ?page=). Só em produção: local e hml não passam aqui.
const CANONICAL_HOST = "app.clinic4us.com.br";
if (["clinic4us.com.br", "www.clinic4us.com.br"].includes(window.location.hostname)) {
  window.location.replace(`https://${CANONICAL_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`);
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// Main app component with routing
const AppContent = () => {
  const { currentPage, navigateTo } = useRouter();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Lista de páginas que requerem autenticação (admin e clients)
  const protectedPages: string[] = [
    "dashboard",
    "schedule",
    "patients",
    "patient-register",
    "patient-quick-register",
    "page-model",
    "admin-plans",
    "admin-profiles",
    "admin-functionalities",
    "admin-entities",
    "admin-faq",
    "admin-professional-types",
    "user-profile",
    "evolutions-print",
    "documents-print",
    "payment-receipt",
    "patient-financial-print",
    "notes-print",
    "assessment-print",
    "entity-settings",
    "entity-users",
    "entity-service-types",
    "entity-professional-types",
    "entity-business-hours",
    "entity-access-restriction",
    "entity-teams",
    "entity-tax-regimes",
    "dre-chart-of-accounts",
    "patient-financial-page",
    "schedule-statuses",
    "clinic-periods",
    "therapy-goal-print",
    "schedule-print",
    "holidays",
    "patient-document-types",
    "health-plan-operators",
    "health-plans",
    "patient-sensitive-data",
    "assessment-forms",
    "assessment-form-editor",
    "assessment-form-family",
    "assessment-application",
    "opportunities",
    "professional-service-values",
    "professional-availability",
    "professional-contracts",
    "service-professional-values",
    "service-values-print",
    "change-password",
    "rooms",
    "birthdays",
    "report-receipts",
    "whatsapp-messages",
    "day-schedule",
    "patient-assistant",
    "assistants",
    "ai-usage",
    "day-schedule-print",
    "schedule-blocks",
    "room-map",
    "ai-feature-terms",
    "ai-agents",
    "ai-tools",
    "workflows",
    "workflow-editor",
    "workflow-catalog",
    "workflow-view",
    "playbook-documents",
    "document-library",
    "document-view",
    "report-schedule-analysis",
    "report-cancellations",
    "report-evolution",
    "report-dre",
    "report-cashflow",
    "report-cash-scenario",
    "cash-scenario",
    "account-categories",
    "payment-origins",
    "report-paid-not-attended",
    "report-day-attendances",
    "report-package-sessions",
    "report-professional-capacity",
    "report-patient-schedules",
    "report-occupancy",
    "report-patient-flow",
    "crm-funnel",
    "crm-settings",
    "crm-campaigns",
    "whatsapp-channels",
    "institutions",
    "cash-book",
    "report-patient-tracking",
    "report-professional-curriculum",
    "report-pending-evolutions",
    "report-professional-productivity",
    "report-professional-statement",
    "report-productivity-print",
    "report-exclusions",
    "contract-templates",
    "contract-template-editor",
    "contract-print",
    "report-patient-contracts",
    "signature-settings",
  ];

  // Verificar se a página atual requer autenticação
  React.useEffect(() => {
    if (
      !isLoading &&
      protectedPages.includes(currentPage) &&
      !isAuthenticated
    ) {
      // Sessão expirada / não autenticado em página protegida: volta ao login mantendo o alias no path (/<alias>).
      redirectToLogin();
    }

    // Conta com senha padrão: trava em change-password até trocar (não permite acessar outras páginas).
    if (
      !isLoading &&
      isAuthenticated &&
      user?.mustChangePassword &&
      currentPage !== "change-password"
    ) {
      navigateTo("change-password");
    }

    // Marcar inicialização como completa após processar a primeira vez
    if (!isLoading) {
      setIsInitializing(false);
    }
  }, [currentPage, isAuthenticated, isLoading, user?.mustChangePassword]);

  // Mostrar loading enquanto verifica autenticação ou inicializa
  if (isLoading || isInitializing) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "1.2rem",
          color: "#6c757d",
        }}
      >
        Carregando...
      </div>
    );
  }

  switch (currentPage) {
    case "login":
      return <Login />;
    case "alias-register":
      return <AliasRegister />;
    case "dashboard":
      return isAuthenticated ? <Dashboard /> : <Login />;
    case "schedule":
      return isAuthenticated ? <ProfessionalSchedule /> : <Login />;
    case "patients":
      return isAuthenticated ? <PatientsList key="patients" /> : <Login />;
    case "patient-quick-register":
      // Item de menu "+ Cadastro rápido": lista de pacientes com o modal já aberto.
      // key distinta força remount na navegação SPA patients <-> patient-quick-register
      // (mesmo componente na mesma posição: sem key o React reusaria o estado).
      return isAuthenticated ? <PatientsList key="patient-quick-register" initialQuickRegisterOpen /> : <Login />;
    case "patient-register":
      return isAuthenticated ? <PatientRegister /> : <Login />;
    case "patient-intake":
      // Página PÚBLICA (RN-112f): aberta pelo link do email, sem sessão
      return <PatientIntake />;
    case "set-password":
    case "cadastro":
      // Página PÚBLICA (RN-005b): convite de primeiro acesso e esqueci-minha-senha.
      // "cadastro" é o apelido usado no botão do template do WhatsApp (RN-005d): a URL não pode
      // conter "password", senão a Meta classifica o template como Autenticação e recusa o link.
      return <SetPassword />;
    case "page-model":
      return isAuthenticated ? <PageModel /> : <Login />;
    case "admin-plans":
      return isAuthenticated ? <AdminPlans /> : <Login />;
    case "admin-profiles":
      return isAuthenticated ? <AdminProfiles /> : <Login />;
    case "admin-functionalities":
      return isAuthenticated ? <AdminFunctionalities /> : <Login />;
    case "admin-entities":
      return isAuthenticated ? <AdminEntities /> : <Login />;
    case "entity-settings":
      return isAuthenticated ? <EntitySettings /> : <Login />;
    case "entity-users":
      return isAuthenticated ? <EntityUsers /> : <Login />;
    case "entity-service-types":
      return isAuthenticated ? <EntityServiceTypes /> : <Login />;
    case "entity-professional-types":
      return isAuthenticated ? <EntityProfessionalTypes /> : <Login />;
    case "entity-business-hours":
      return isAuthenticated ? <EntityBusinessHours /> : <Login />;
    case "entity-access-restriction":
      return isAuthenticated ? <EntityAccessRestriction /> : <Login />;
    case "report-schedule-analysis":
      return isAuthenticated ? <ReportScheduleAnalysis /> : <Login />;
    case "report-cancellations":
      return isAuthenticated ? <ReportCancellations /> : <Login />;
    case "report-evolution":
      return isAuthenticated ? <ReportEvolution /> : <Login />;
    case "report-dre":
      return isAuthenticated ? <ReportDre /> : <Login />;
    case "report-cashflow":
      return isAuthenticated ? <ReportCashFlow /> : <Login />;
    case "report-cash-scenario":
      return isAuthenticated ? <ReportCashScenario /> : <Login />;
    case "cash-scenario":
      return isAuthenticated ? <CashScenario /> : <Login />;
    case "account-categories":
      return isAuthenticated ? <AccountCategories /> : <Login />;
    case "payment-origins":
      return isAuthenticated ? <PaymentOrigins /> : <Login />;
    case "report-paid-not-attended":
      return isAuthenticated ? <ReportPaidNotAttended /> : <Login />;
    case "report-day-attendances":
      return isAuthenticated ? <ReportDayAttendances /> : <Login />;
    case "report-package-sessions":
      return isAuthenticated ? <ReportPackageSessions /> : <Login />;
    case "report-professional-capacity":
      return isAuthenticated ? <ReportProfessionalCapacity /> : <Login />;
    case "report-patient-schedules":
      return isAuthenticated ? <ReportPatientSchedules /> : <Login />;
    case "report-occupancy":
      return isAuthenticated ? <ReportOccupancy /> : <Login />;
    case "report-patient-flow":
      return isAuthenticated ? <ReportPatientFlow /> : <Login />;
    case "rooms":
      return isAuthenticated ? <Rooms /> : <Login />;
    case "birthdays":
      return isAuthenticated ? <Birthdays /> : <Login />;
    case "report-receipts":
      return isAuthenticated ? <ReportReceipts /> : <Login />;
    case "whatsapp-messages":
      return isAuthenticated ? <WhatsAppMessages /> : <Login />;
    case "patient-assistant":
      return isAuthenticated ? <PatientAssistant /> : <Login />;
    // Assistentes é um app em modal (AssistantsHost): o link antigo abre o app sobre o Dashboard; na
    // janela separada não há nada por baixo do app
    case "ai-usage":
      return isAuthenticated ? <AiUsage /> : <Login />;
    case "assistants":
      if (!isAuthenticated) return <Login />;
      return isAssistantsPopout() ? null : <Dashboard />;
    case "day-schedule":
      return isAuthenticated ? <DaySchedule /> : <Login />;
    case "day-schedule-print":
      return isAuthenticated ? <DaySchedulePrint /> : <Login />;
    case "schedule-blocks":
      return isAuthenticated ? <ScheduleBlocks /> : <Login />;
    case "checkin":
      return <Checkin />;
    case "room-map":
      return isAuthenticated ? <RoomMap /> : <Login />;
    case "entity-teams":
      return isAuthenticated ? <EntityTeams /> : <Login />;
    case "entity-tax-regimes":
      return isAuthenticated ? <EntityTaxRegimes /> : <Login />;
    case "ai-feature-terms":
      return isAuthenticated ? <AiFeatureTerms /> : <Login />;
    case "ai-agents":
      return isAuthenticated ? <AiAgents /> : <Login />;
    case "ai-tools":
      return isAuthenticated ? <AiTools /> : <Login />;
    case "workflows":
      return isAuthenticated ? <Workflows /> : <Login />;
    case "workflow-editor":
      // Página-filha (fora do menu): recebe a versão em edição por ?versionId=
      return isAuthenticated ? <WorkflowEditor /> : <Login />;
    case "workflow-catalog":
      return isAuthenticated ? <WorkflowCatalog /> : <Login />;
    case "workflow-view":
      // Página-filha (fora do menu): recebe o fluxo consultado por ?workflowId=
      return isAuthenticated ? <WorkflowView /> : <Login />;
    case "playbook-documents":
      return isAuthenticated ? <PlaybookDocuments /> : <Login />;
    case "document-library":
      return isAuthenticated ? <DocumentLibrary /> : <Login />;
    case "document-view":
      // Página-filha (fora do menu): recebe o documento consultado por ?documentId=
      return isAuthenticated ? <DocumentView /> : <Login />;
    case "dre-chart-of-accounts":
      return isAuthenticated ? <DreChartOfAccounts /> : <Login />;
    case "patient-financial-page":
      return isAuthenticated ? <PatientFinancialPage /> : <Login />;
    case "schedule-statuses":
      return isAuthenticated ? <ScheduleStatuses /> : <Login />;

    case "clinic-periods":
      return isAuthenticated ? <ClinicPeriods /> : <Login />;

    case "schedule-print":
      // Página-filha (fora do menu): impressão da agenda por ?from=, ?to= e ?professionals=
      return isAuthenticated ? <SchedulePrint /> : <Login />;
    case "therapy-goal-print":
      // Página-filha (fora do menu): impressão do objetivo por ?goalId= e ?patientId=
      return isAuthenticated ? <TherapyGoalPrint /> : <Login />;
    case "holidays":
      return isAuthenticated ? <Holidays /> : <Login />;
    case "patient-document-types":
      return isAuthenticated ? <PatientDocumentTypes /> : <Login />;
    case "health-plan-operators":
      return isAuthenticated ? <HealthPlanOperators /> : <Login />;
    case "health-plans":
      return isAuthenticated ? <HealthPlans /> : <Login />;
    case "patient-sensitive-data":
      return isAuthenticated ? <PatientSensitiveData /> : <Login />;
    case "assessment-forms":
      return isAuthenticated ? <AssessmentForms /> : <Login />;
    case "assessment-form-editor":
      // Página-filha (fora do menu): recebe o formulário por ?formId=
      return isAuthenticated ? <AssessmentFormEditor /> : <Login />;
    case "assessment-form-family":
      // Página-filha (fora do menu): histórico de versões do instrumento por ?formId=
      return isAuthenticated ? <AssessmentFormFamily /> : <Login />;
    case "signature-settings":
      return isAuthenticated ? <SignatureSettings /> : <Login />;
    case "contract-templates":
      return isAuthenticated ? <ContractTemplates /> : <Login />;
    case "contract-template-editor":
      // Página-filha (fora do menu): editor de uma versão do modelo de contrato por ?versionId=
      return isAuthenticated ? <ContractTemplateEditor /> : <Login />;
    case "contract-print":
      // Página-filha (fora do menu): impressão de um contrato emitido por ?contractId=
      return isAuthenticated ? <ContractPrint /> : <Login />;
    case "report-patient-contracts":
      return isAuthenticated ? <ReportPatientContracts /> : <Login />;
    case "assessment-application":
      // Página-filha (fora do menu): preenchimento de avaliação por ?applicationId=
      return isAuthenticated ? <AssessmentApplication /> : <Login />;
    case "opportunities":
      return isAuthenticated ? <Opportunities /> : <Login />;
    case "crm-funnel":
      return isAuthenticated ? <CrmFunnel /> : <Login />;
    case "crm-settings":
      return isAuthenticated ? <CrmSettings /> : <Login />;
    case "crm-campaigns":
      return isAuthenticated ? <CrmCampaigns /> : <Login />;
    case "whatsapp-channels":
      return isAuthenticated ? <WhatsAppChannels /> : <Login />;
    case "professional-service-values":
      return isAuthenticated ? <ProfessionalServiceValues /> : <Login />;
    case "professional-availability":
      return isAuthenticated ? <ProfessionalAvailability /> : <Login />;
    case "professional-contracts":
      return isAuthenticated ? <ProfessionalContracts /> : <Login />;
    case "service-professional-values":
      return isAuthenticated ? <ServiceProfessionalValues /> : <Login />;
    case "service-values-print":
      return isAuthenticated ? <ServiceValuesPrint /> : <Login />;
    case "change-password":
      return isAuthenticated ? <ChangePassword /> : <Login />;
    case "admin-faq":
      return isAuthenticated ? <AdminFaq /> : <Login />;
    case "admin-professional-types":
      return isAuthenticated ? <AdminProfessionalTypes /> : <Login />;
    case "faq":
      return <Faq />;
    case "user-profile":
      return isAuthenticated ? <UserProfile /> : <Login />;
    case "documents-print":
      return isAuthenticated ? <DocumentsPrint /> : <Login />;
    case "payment-receipt":
      // Página-filha (fora do menu): recibo de pagamento por ?id=
      return isAuthenticated ? <PaymentReceiptPrint /> : <Login />;
    case "patient-financial-print":
      // Página-filha (fora do menu): extrato das sessões filtradas no caixa, por ?patientId=
      return isAuthenticated ? <PatientFinancialPrint /> : <Login />;
    case "evolutions-print":
      // Página-filha (fora do menu): impressão das evoluções por ?patientId=&ids=
      return isAuthenticated ? <EvolutionsPrint /> : <Login />;
    case "assessment-print":
      // Página-filha (fora do menu): impressão da avaliação por ?applicationId=
      return isAuthenticated ? <AssessmentPrint /> : <Login />;
    case "notes-print":
      return isAuthenticated ? <NotesPrint /> : <Login />;
    case "institutions":
      return isAuthenticated ? <Institutions /> : <Login />;

    case "cash-book":
      return isAuthenticated ? <CashBook /> : <Login />;

    case "report-patient-tracking":
      return isAuthenticated ? <ReportPatientTracking /> : <Login />;

    case "report-professional-curriculum":
      return isAuthenticated ? <ReportProfessionalCurriculum /> : <Login />;
    case "report-pending-evolutions":
      return isAuthenticated ? <ReportPendingEvolutions /> : <Login />;
    case "report-professional-productivity":
      return isAuthenticated ? <ReportProfessionalProductivity /> : <Login />;
    case "report-professional-statement":
      return isAuthenticated ? <ReportProfessionalStatement /> : <Login />;

    case "report-productivity-print":
      return isAuthenticated ? <ProductivityPrint /> : <Login />;
    case "report-exclusions":
      return isAuthenticated ? <ReportExclusions /> : <Login />;
    case "landing":
    default:
      // Landing pública OCULTA (decisão do Hilton, 04/09/2026, véspera da abertura): a raiz
      // vai direto ao login; /<alias> abre o login com a clínica preenchida quando existe, e
      // alias desconhecido cai no login com o campo editável. O componente LandingPage fica
      // no repositório para voltar quando o produto for oferecido a outras clínicas.
      return <Login />;
  }
};

// Root component with providers
const App = () => {
  return (
    <ThemeProvider theme={globalTheme}>
      <AuthProvider>
        <RouterProvider>
          <AppContent />
          <ChatLauncher />
          <AssistantsHost />
          <SessionLockDialog />
        </RouterProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
